import { useCallback, useEffect, useState } from 'react';
import { createQuizRevisionConversation } from '../services/quizRevisionService';
import { buildRevisionQuizQuestionData, mapRevisionLearningObjectives } from '../utils/buildRevisionQuizQuestionData';

// Stable default: a new [] on every render would re-create the conversations.
const EMPTY_QUESTIONS = [];

/**
 * Hook to manage quiz revision conversation creation
 * @param {Object} job - Quiz job object with metadata
 * @param {string} userId - External user ID
 * @param {boolean} autoCreate - Auto-create on mount (default: false)
 * @param {Object} questionsLearningObjectives - Map of questionGuid to learning objectives array
 * @param {Array} frozenQuestions - Questions as the student saw them, to fill in answers stored without their question
 * @returns {Object} { isCreating, conversations, createConversations }
 */
export function useQuizRevisionConversations(job, userId, autoCreate = false, questionsLearningObjectives = {}, frozenQuestions = EMPTY_QUESTIONS) {
  const [isCreating, setIsCreating] = useState(autoCreate);
  const [conversations, setConversations] = useState({});

  const createConversations = useCallback(async () => {
    if (!job?.metadata || !userId) {
      return;
    }

    setIsCreating(true);

    const parseJobDataToArray = (jsonString) => {
      try {
        const parsed = JSON.parse(jsonString);

        if (Array.isArray(parsed)) {
          return parsed;
        }

        if (typeof parsed === 'object' && parsed !== null) {
          return Object.keys(parsed)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => parsed[key]);
        }

        throw new Error('Unexpected format');
      } catch (error) {
        console.error('Failed to parse:', error.message);
        return [];
      }
    };

    try {
      const metadata = JSON.parse(job.metadata);
      const jobData = parseJobDataToArray(job.data);
      const frozenQuestionList = Array.isArray(frozenQuestions) ? frozenQuestions : [];

      if (!metadata.answers || metadata.answers.length === 0) {
        console.warn('No quiz questions found in metadata');
        return;
      }

      // Create payloads for each question. Skipped questions are stored as null
      // in metadata.answers (the array is index-aligned with the questions), so
      // they are left without a revision conversation instead of aborting the job.
      const payloads = metadata.answers.map((answer, index) => {
        if (!answer) {
          console.warn('[useQuizRevisionConversations] Question skipped by the student, no revision conversation created. Index:', index);
          return null;
        }
        // TODO: FALLBACK BASED ON INDEX - THIS HAS RELIABILITY ISSUES TO REVIEW.
        // Reason for the fallback is that the metadata in the job has multiple formats (2 identified) and the questionGuid is not available in one of the 2 formats
        // The metadata and data are saved from the front and there are multiple locations where the student can solve activities and the hypothesis is that the way the metadata and data are saved are different.
        const questionGuid = answer.questionGuid || jobData[index]?.guid;
        if (!questionGuid) {
          console.warn('[useQuizRevisionConversations] Missing questionGuid in answer payload, no revision conversation created. Index:', index);
          return null;
        }
        const frozenQuestion = frozenQuestionList.find((question) => (question?.question_guid || question?.guid) === questionGuid) || frozenQuestionList[index];
        const quizQuestionData = buildRevisionQuizQuestionData({ answerEntry: answer, frozenQuestion, questionGuid });
        if (!quizQuestionData) {
          console.warn('[useQuizRevisionConversations] No question data for the answer, no revision conversation created. Index:', index);
          return null;
        }

        return {
          externalUserId: userId,
          quizJobGuid: job.guid,
          questionReferenceId: questionGuid,
          quizQuestionData,
          learningObjectives: mapRevisionLearningObjectives(questionsLearningObjectives[questionGuid]),
        };
      });

      // Create all conversations in parallel
      const promises = payloads.filter(Boolean).map((payload) =>
        createQuizRevisionConversation(payload)
          .then((response) => {
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'ready',
              conversationId: response.conversationId,
              error: null,
            };
          })
          .catch((error) => {
            console.error('[useQuizRevisionConversations] Failed for question:', payload.questionReferenceId, error);
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'failed',
              conversationId: null,
              error: error.message,
            };
          })
      );

      const results = await Promise.all(promises);

      // Build conversations state map
      const conversationsMap = {};
      results.forEach((result) => {
        conversationsMap[result.questionReferenceId] = result;
      });

      setConversations(conversationsMap);
    } catch (error) {
      console.error('Failed to create quiz revision conversations:', error);
    } finally {
      setIsCreating(false);
    }
  }, [job, userId, questionsLearningObjectives, frozenQuestions]);

  // Auto-create on mount if enabled
  useEffect(() => {
    if (autoCreate && job && userId) {
      createConversations();
    }
  }, [autoCreate, job, userId, createConversations]);

  return {
    isCreating,
    conversations,
    createConversations, // Expose for manual retry
  };
}
